import { Settings as SettingsIcon, User, Bell, Shield, Database } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { ComingSoonWatermark } from '@/components/ComingSoonWatermark';
import { useNavigation } from '@/contexts/NavigationContext';
import { cn } from '@/lib/utils';

export function SettingsPage() {
  const { sectionColor } = useNavigation();

  return (
    <div className="space-y-6">
      <ComingSoonWatermark
        pageKey="settings-management"
        icon={SettingsIcon}
        title="Settings Management Coming Soon"
        description="Comprehensive settings for user profiles, notifications, security, and organization configuration will be available soon."
      />
      {/* Page Header */}
      <div>
        <h1 className={cn("text-3xl font-bold", sectionColor)}>Settings</h1>
        <p className="text-neutral-400 mt-1">
          Configure your organization and user preferences
        </p>
      </div>

      {/* Settings Categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-6 hover:border-primary-500/50 transition-colors cursor-pointer">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-primary-500/20 rounded-lg">
              <User className="text-primary-400" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-1">User Profile</h3>
              <p className="text-sm text-neutral-400">
                Manage your personal information and preferences
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6 hover:border-primary-500/50 transition-colors cursor-pointer">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-primary-500/20 rounded-lg">
              <Bell className="text-primary-400" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-1">Notifications</h3>
              <p className="text-sm text-neutral-400">
                Configure alert thresholds and notification preferences
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6 hover:border-primary-500/50 transition-colors cursor-pointer">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-primary-500/20 rounded-lg">
              <Shield className="text-primary-400" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-1">Security</h3>
              <p className="text-sm text-neutral-400">
                Manage authentication, permissions, and access control
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6 hover:border-primary-500/50 transition-colors cursor-pointer">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-primary-500/20 rounded-lg">
              <Database className="text-primary-400" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-1">Organization</h3>
              <p className="text-sm text-neutral-400">
                Configure organization settings and integrations
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Settings Details */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Account Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-neutral-800/50">
            <div>
              <p className="text-white font-medium">Email Notifications</p>
              <p className="text-sm text-neutral-400">Receive risk alerts via email</p>
            </div>
            <button className="bg-primary-500 w-12 h-6 rounded-full relative">
              <span className="absolute right-1 top-1 bg-white w-4 h-4 rounded-full"></span>
            </button>
          </div>
          <div className="flex items-center justify-between py-3 border-b border-neutral-800/50">
            <div>
              <p className="text-white font-medium">Two-Factor Authentication</p>
              <p className="text-sm text-neutral-400">Add extra security to your account</p>
            </div>
            <button className="bg-neutral-700 w-12 h-6 rounded-full relative">
              <span className="absolute left-1 top-1 bg-neutral-400 w-4 h-4 rounded-full"></span>
            </button>
          </div>
          <div className="flex items-center justify-between py-3 border-b border-neutral-800/50">
            <div>
              <p className="text-white font-medium">Risk Assessment Reminders</p>
              <p className="text-sm text-neutral-400">Get reminded of upcoming assessments</p>
            </div>
            <button className="bg-primary-500 w-12 h-6 rounded-full relative">
              <span className="absolute right-1 top-1 bg-white w-4 h-4 rounded-full"></span>
            </button>
          </div>
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="text-white font-medium">Dark Mode</p>
              <p className="text-sm text-neutral-400">Use dark theme (currently enabled)</p>
            </div>
            <button className="bg-primary-500 w-12 h-6 rounded-full relative">
              <span className="absolute right-1 top-1 bg-white w-4 h-4 rounded-full"></span>
            </button>
          </div>
        </div>
      </Card>

      {/* Organization Info */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Organization Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Organization Name</label>
            <input type="text" className="form-input" value="Line4 Observability" disabled />
          </div>
          <div>
            <label className="form-label">Industry</label>
            <input type="text" className="form-input" value="Risk Management & Compliance" disabled />
          </div>
          <div>
            <label className="form-label">Primary Contact</label>
            <input type="text" className="form-input" value="admin@line4.ai" disabled />
          </div>
          <div>
            <label className="form-label">License Type</label>
            <input type="text" className="form-input" value="Enterprise" disabled />
          </div>
        </div>
      </Card>
    </div>
  );
}
